/**
 * Dashboard Preview Step
 * Shows compact mockup of dashboard with progress tracking
 */

import { Calendar, ArrowRight } from 'lucide-react';
import { OnboardingStep } from '../OnboardingStep';
import { Card, CardContent } from '@/components/ui/card';

function MockRadialChart() {
  return (
    <div className="relative w-24 h-24 shrink-0">
      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke="var(--color-muted)"
          strokeWidth="8"
        />
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke="var(--color-primary)"
          strokeWidth="8"
          strokeDasharray={`${2 * Math.PI * 40 * 0.65} ${2 * Math.PI * 40}`}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <span className="text-lg font-bold text-foreground">65%</span>
      </div>
    </div>
  );
}

function DashboardMockup() {
  return (
    <div className="w-full max-w-md mx-auto space-y-3 p-3 pointer-events-none select-none">
      {/* Main Progress Card */}
      <Card className="bg-card border-border overflow-hidden">
        <CardContent className="p-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-1">
            ความคืบหน้า Khatm
          </p>
          <h2 className="text-lg font-bold text-foreground mb-3">
            ขอให้ง่ายดาย, คุณ!
          </h2>

          <div className="flex flex-row items-stretch gap-4">
            {/* Left: Stats */}
            <div className="flex flex-col min-w-0 flex-1">
              <div className="flex items-center gap-3">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    หน้า
                  </p>
                  <p className="text-lg font-bold text-foreground leading-tight">
                    392/604
                  </p>
                </div>
                <div className="w-px self-stretch shrink-0 bg-border" />
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Khatm
                  </p>
                  <p className="text-lg font-bold text-foreground leading-tight">
                    0/1
                  </p>
                </div>
              </div>
              <span className="mt-2 inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide w-fit text-white bg-green-500">
                ตามแผน
              </span>
            </div>

            {/* Right: Chart */}
            <MockRadialChart />
          </div>

          {/* Additional Info */}
          <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              <span>เหลืออีก 15 วัน</span>
            </div>
            <div className="flex items-center gap-1.5">
              <ArrowRight className="h-3.5 w-3.5" />
              <span>หน้าถัดไป: 393</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Daily Progress Bar */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <h3 className="text-sm font-semibold text-foreground">
            ความคืบหน้าวันนี้
          </h3>
          <span className="text-xs text-muted-foreground">
            15/20 หน้า
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-all"
            style={{ width: '75%' }}
          />
        </div>
      </div>
    </div>
  );
}

export function DashboardPreviewStep() {
  return (
    <OnboardingStep
      title="ติดตามความคืบหน้าแบบเรียลไทม์"
      description="ดูสถิติการอ่าน เป้าหมายรายวัน และเซสชันทั้งหมดในหน้าแดชบอร์ด"
      illustration={<DashboardMockup />}
    />
  );
}

export default DashboardPreviewStep;
